import React, { useState, useEffect } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import set from 'lodash/set';
import keyBy from 'lodash/keyBy';
import MUIDataTable from 'mui-datatables';
import {
  Tooltip,
  Grid,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import { Stop, Check } from '@mui/icons-material';
import pickupZoneActions from '../../../actions/pickupZones';
import vehicleZoneActions from '../../../actions/vehicleZones';
import * as snackbarActions from '../../../actions/snackbar';
import WrappedSpinner from '../../common/wrapped-spinner';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import CustomToolbar from '../custom-toolbar';
import BulkUploadModal from '../bulk-upload-modal';
import BulkDownloadPickupZones from '../bulk-download-pickup-zones';
import WarnBeforeDeleting from '../../common/warn-before-deleting';
import VehicleZoneMap from './VehicleZoneMap';
import ZoneActionDropdown from './zone-action-dropdown';
import {
  getAutoUnderMinIcon,
  getAutoOverMaxIcon,
  getAutoScooterCaseIcon,
  getAutoEmailIcon,
} from './svg-icons';
import Modal from '../../common/modal';
import {
  editableVehicleZoneCategories,
  VehicleZonesStatusNames,
  VehicleZoneTypes,
  convertPolyToCoords,
} from '../../../lib/utils';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
};

export const RED = '#FF0000';
export const ORANGE = '#FF8C00';
export const LIGHT_GREEN = '#90EE90';

const ZONE_TABLE_TITLE = 'Vehicle Zones';
const DELETE_WARN = 'Are you sure you want to delete this Vehicle Zone?';
const DELETE_CONFIRM = 'Successfully deleted Vehicle Zone';
const DELETE_ERROR = 'Error deleting Vehicle Zone';
const NEEDS_COORDS = 'Please draw one complete new zone. Use the "Draw Zone" button at the top of the map to start drawing, and click the highlighted starting point to finish the zone.';
const NEEDS_NAME = 'Please provide a name. Use the text prompt at the top to give this zone a name.';
const NEEDS_MAX = 'Please provide maximum scooter count. Use the corresponding text input field in the left sidebar.';
const NEEDS_MAX_GREATER_MIN = 'Maximum scooter count value should be greater then minimum scooter count value.';

const UPDATE_ERROR = 'Error updating zone';
const UPDATE_SUCCESS = 'Successfully updated zone';
const CREATE_SUCCESS = 'Successfully created zone';

const DEACTIVATE_SUCCESS = 'Successfully set Vehicle Zone to: ';

const NAME = 'Name';
const TYPE = 'Type';
const STATUS = 'Status';
const COUNT = 'Vehicle count';
const TARGET = 'Target';
const MINIMUM = 'Min';
const MAXIMUM = 'Max';
const CREATE_CASE = 'Create a case';
const CREATE_CASE_OVER_MAX = 'Create a case over max';
const CREATE_CASE_UNDER_MIN = 'Create a case under min';
const CREATE_CASE_HIGH_RISK = 'Create a high rish case';
const CREATE_CASE_EMAIL = 'Send email';
const CREATE_TASK = 'Create a task';
const ACTIONS = 'Actions';

function getTableOptions(props, addZone, editZone, uploadBulkZones, downloadBulkZones) {
  const basicOptions = props.tableOptions;
  basicOptions.download = false;

  basicOptions.onRowClick = (rowData) => {
    editZone(rowData[0]);
  };

  basicOptions.customToolbar = () => (
    <>
      <CustomToolbar onClick={downloadBulkZones} type="Download" />
      <CustomToolbar onClick={uploadBulkZones} type="Upload" />
      <CustomToolbar onClick={addZone} type="Add" />
    </>
  );

  basicOptions.customSearch = (searchQuery, currentRow, columns) => (
    currentRow.reduce((res, column) => {
      // Custom search only for string columns and name as part of custom column
      const type = typeof column;
      if (type === 'string' || type === 'object' && !!column?.name) {
        const value = (column.name || column).toLowerCase();
        return res || value.includes(searchQuery.toLowerCase());
      }
      return res;
    }, false)
  );

  return basicOptions;
}

function getTableColumns(
  props,
  marketId,
  vehicleZonesById,
  onDelete,
  editZoneMap,
  onActivityToggle,
) {
  return props.addToColumnData([
    {
      name: 'id',
      options: {
        display: 'excluded',
        filter: false,
      },
    },
    {
      name: NAME,
      options: {
        filter: false,
        customBodyRender: ({ name, hexColor }) => (
          <Grid container alignItems="center" wrap="nowrap" sx={{ minWidth: 120 }}>
            <Stop
              sx={{ width: '1.25em', height: '1.25em', mr: 1 }}
              style={{ color: hexColor || 'white' }}
            />
            <Typography>{name}</Typography>
          </Grid>
        ),
      },
    },
    {
      name: TYPE,
      options: {
        customFilterListOptions: {
          render: (type) => `${TYPE}: ${type || 'default'}`,
        },
        filterOptions: {
          renderValue: (type) => (type || 'default'),
        },
      },
    },
    {
      name: STATUS,
      options: {
        customFilterListOptions: {
          render: (status) => `${STATUS}: ${status}`,
        },
      },
    },
    {
      name: COUNT,
      options: {
        filter: false,
        customBodyRender: ({ id, count }) => (
          <Link
            style={styles.link}
            to={`/operator/markets/${marketId}/vehicles?pickupZones=${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {count}
          </Link>
        ),
        sortCompare: (order) => (
          (v1, v2) => (v1.data.count - v2.data.count) * (order === 'asc' ? 1 : -1)
        ),
      },
    },
    // uncomment when equity zone work is picked back up
    // {
    //   name: TARGET,
    //   options: { filter: false },
    // },
    // {
    //   name: MINIMUM,
    //   options: {
    //     filter: false,
    //     customBodyRender: (min) => (
    //       <Typography sx={{ color: min ? 'default' : 'grey.500' }}>
    //         {min}
    //       </Typography>
    //     ),
    //   },
    // },
    // {
    //   name: MAXIMUM,
    //   options: { filter: false },
    // },
    // {
    //   name: CREATE_CASE,
    //   options: {
    //     filter: false,
    //     customBodyRender: (automation) => (
    //       <Grid container justifyContent="center" alignItems="center" wrap="nowrap" gap="1">
    //         <Grid item xs="3" sx={{ minWidth: 28 }}>
    //           {automation.createCaseWhenUnderMin && (
    //             <img src={getAutoUnderMinIcon()} alt="Create case when under min" />
    //           )}
    //         </Grid>
    //         <Grid item xs="3" sx={{ minWidth: 28 }}>
    //           {automation.createCaseWhenOverMax && (
    //             <img src={getAutoOverMaxIcon()} alt="Create case when over max" />
    //           )}
    //         </Grid>
    //         <Grid item xs="3" sx={{ minWidth: 28 }}>
    //           {automation.createCaseForScooterInHighRiskZone && (
    //             <img
    //               src={getAutoScooterCaseIcon()}
    //               alt="Create case for scooter in high risk zone"
    //             />
    //           )}
    //         </Grid>
    //         <Grid item xs="3" sx={{ minWidth: 28 }}>
    //           {automation.sendEmailWhenCaseCreated && (
    //             <img src={getAutoEmailIcon()} alt="Send email when case was created" />
    //           )}
    //         </Grid>
    //       </Grid>
    //     ),
    //   },
    // },
    // {
    //   name: CREATE_CASE_UNDER_MIN,
    //   options: {
    //     display: 'excluded',
    //     customFilterListOptions: {
    //       render: (value) => `${CREATE_CASE_UNDER_MIN}: ${value ? 'Yes' : 'No'}`,
    //     },
    //     filterOptions: {
    //       renderValue: (value) => (value ? 'Yes' : 'No'),
    //     },
    //   },
    // },
    // {
    //   name: CREATE_CASE_OVER_MAX,
    //   options: {
    //     display: 'excluded',
    //     customFilterListOptions: {
    //       render: (value) => `${CREATE_CASE_OVER_MAX}: ${value ? 'Yes' : 'No'}`,
    //     },
    //     filterOptions: {
    //       renderValue: (value) => (value ? 'Yes' : 'No'),
    //     },
    //   },
    // },
    // {
    //   name: CREATE_CASE_HIGH_RISK,
    //   options: {
    //     display: 'excluded',
    //     customFilterListOptions: {
    //       render: (value) => `${CREATE_CASE_HIGH_RISK}: ${value ? 'Yes' : 'No'}`,
    //     },
    //     filterOptions: {
    //       renderValue: (value) => (value ? 'Yes' : 'No'),
    //     },
    //   },
    // },
    // {
    //   name: CREATE_CASE_EMAIL,
    //   options: {
    //     display: 'excluded',
    //     customFilterListOptions: {
    //       render: (value) => `${CREATE_CASE_EMAIL}: ${value ? 'Yes' : 'No'}`,
    //     },
    //     filterOptions: {
    //       renderValue: (value) => (value ? 'Yes' : 'No'),
    //     },
    //   },
    // },
    // {
    //   name: CREATE_TASK,
    //   options: {
    //     customBodyRender: (value) => (
    //       <Grid container justifyContent="center">
    //         {value && (<Check />)}
    //       </Grid>
    //     ),
    //     customFilterListOptions: {
    //       render: (value) => `${CREATE_TASK}: ${value ? 'Yes' : 'No'}`,
    //     },
    //     filterOptions: {
    //       renderValue: (value) => (value ? 'Yes' : 'No'),
    //     },
    //   },
    // },
    {
      name: ACTIONS,
      options: {
        filter: false,
        customBodyRender: (vehicleZoneId) => {
          const status = get(vehicleZonesById, `${vehicleZoneId}.status`);
          const isActive = status === VehicleZonesStatusNames.ACTIVE;

          return (
            <ZoneActionDropdown
              zoneId={vehicleZoneId}
              active={isActive}
              onActivityToggle={onActivityToggle}
              onEdit={editZoneMap}
              onDelete={onDelete}
            />
          );
        },
      },
    },
  ], props.selectedFilters, props.primarySort);
}

function getTableData(vehicleZones) {
  if (!vehicleZones) return [];
  return vehicleZones.map((vehicleZone) => {
    const {
      _id, name, type, status,
      hexColor, vehicleCount,
      target, minimum, maximum,
      automation = {},
    } = vehicleZone;
    const data = [
      _id,
      { name, hexColor },
      type,
      status,
      { id: _id, count: vehicleCount },
      // uncomment when equity zone work is picked back up
      // target,
      // minimum,
      // maximum,
      // automation,
      // automation.createCaseWhenUnderMin || false,
      // automation.createCaseWhenOverMax || false,
      // automation.createCaseForScooterInHighRiskZone || false,
      // automation.sendEmailWhenCaseCreated || false,
      // automation.createTaskForScooterInHighRiskZone || false,
      _id,
    ];
    return data;
  });
}

function VehicleZonesContainer(props) {
  const {
    pickupZones = [],
    fetchVehicleZones,
    fetchPickupZones,
    updatePickupZone,
    createPickupZone,
    deletePickupZone,
    bulkUploadPickupZones,
    pickupZonesLoading,
    selectedMarketId: marketId,
    showErrorSnackbar,
    showSuccessSnackbar,
    selectedMarket,
  } = props;

  useEffect(() => {
    fetchVehicleZones(marketId);
    fetchPickupZones(marketId);
  }, [marketId]);

  const [warnBeforeDeleting, setWarnBeforeDeleting] = useState(false);
  const [currentZone, setCurrentZone] = useState(null);
  const [bulkFormOpen, setBulkFormOpen] = useState(false);
  const [donwloadOptionsDisplayed, setDonwloadOptionsDisplayed] = useState(false);
  const filteredZones = pickupZones.filter(
    (zone) => zone.status === VehicleZonesStatusNames.ACTIVE,
  );

  const validateAutomation = (type, automation = {}) => {
    // we don't send emails if we don't create cases

    if (type === VehicleZoneTypes.EQUITY) {
      if (!automation.createCaseWhenUnderMin && !automation.createCaseWhenOverMax) {
        // eslint-disable-next-line no-param-reassign
        automation.sendEmailWhenCaseCreated = false;
      }
    }

    if (type === VehicleZoneTypes.HIGH_RISK) {
      if (!automation.createCaseForScooterInHighRiskZone) {
        // eslint-disable-next-line no-param-reassign
        automation.sendEmailWhenCaseCreated = false;
      }
    }
  };

  const handleChange = (event) => {
    const newZone = {
      ...currentZone,
      [event.target.name]: event.target.value,
    };

    validateAutomation(newZone.type, newZone.automation);
    setCurrentZone(newZone);
  };

  const handleAutomationChange = (event) => {
    const { name, checked } = event.target;
    const automation = currentZone.automation || {};

    automation[name] = checked;
    validateAutomation(currentZone.type, automation);

    const newZone = {
      ...currentZone,
      automation,
    };

    setCurrentZone(newZone);
  };

  const handleCreateBoundary = (polygon) => {
    const zoneCoords = convertPolyToCoords(polygon);
    zoneCoords[zoneCoords.length] = zoneCoords[0];

    const newZone = {
      ...currentZone,
      boundary: {
        coordinates: [[zoneCoords]],
        type: 'MultiPolygon',
      },
    };

    setCurrentZone(newZone);
  };

  const handleEditBoundary = (polygon) => {
    const zoneCoords = convertPolyToCoords(polygon);
    if (zoneCoords[zoneCoords.length] !== zoneCoords[0]) {
      zoneCoords[zoneCoords.length] = zoneCoords[0];
    }
    const newZone = {
      ...currentZone,
    };
    set(newZone, 'boundary.coordinates[0][0]', zoneCoords);
    setCurrentZone(newZone);
  };

  const closeMapModal = (success, edit) => {
    setCurrentZone(null);

    if (edit) {
      if (!success) {
        showErrorSnackbar(UPDATE_ERROR);
      } else {
        showSuccessSnackbar(UPDATE_SUCCESS);
      }
    } else if (!success) {
      showErrorSnackbar(UPDATE_ERROR);
    } else {
      showSuccessSnackbar(CREATE_SUCCESS);
    }
  };

  const handleZoneSave = () => {
    if (!get(currentZone, 'boundary.coordinates')) {
      return showErrorSnackbar(NEEDS_COORDS);
    }
    if (!currentZone.name) {
      return showErrorSnackbar(NEEDS_NAME);
    }
    if (currentZone.type === VehicleZoneTypes.EQUITY) {
      if (!currentZone.maximum && (!!currentZone.minimum
        || !!get(currentZone, 'automation.createCaseWhenOverMax'))) {
        return showErrorSnackbar(NEEDS_MAX);
      }

      if (!!currentZone.maximum && (currentZone.maximum <= currentZone.minimum)) {
        return showErrorSnackbar(NEEDS_MAX_GREATER_MIN);
      }
    }

    const newZone = { ...currentZone };
    if (!newZone.marketId) set(newZone, 'marketId', marketId);

    // update action switches arg order
    const updateAction = (currentZone && currentZone._id)
      ? (zoneArgs, zoneId) => updatePickupZone(zoneId, zoneArgs)
      : (zoneArgs) => createPickupZone(zoneArgs);

    updateAction(newZone, newZone._id)
      .then((data) => {
        if (data) {
          closeMapModal(true, currentZone && currentZone._id);
        } else {
          closeMapModal(false, false);
        }
      })
      .then(() => fetchPickupZones(marketId))
      .catch((error) => {
        const errMsg = get(error, 'response.data.message', '');
        showErrorSnackbar(`Error from server: ${errMsg}`);
      });
  };

  const vehicleZonesById = keyBy(pickupZones, (vehicleZone) => vehicleZone._id);

  const optionsAddClickHandler = () => {
    setCurrentZone({});
  };

  const optionsEditClickHandler = (id) => {
    setCurrentZone(vehicleZonesById[id]);
  };

  const optionsMapClickHandler = (id) => {
    setCurrentZone(vehicleZonesById[id]);
  };

  const deleteClickHandler = (id) => {
    setCurrentZone(vehicleZonesById[id]);
    setWarnBeforeDeleting(true);
  };

  const handleOpenBulkUploadForm = () => {
    setBulkFormOpen(true);
  };

  const handleCloseBulkUploadForm = () => {
    setBulkFormOpen(false);
  };

  const handleOpenDownloadOptions = () => {
    setDonwloadOptionsDisplayed(true);
  };

  const handleCloseDownloadOptions = () => {
    setDonwloadOptionsDisplayed(false);
  };

  const handleSaveBulkUploadForm = (zonesData, disableOldZones) => {
    bulkUploadPickupZones(marketId, zonesData, disableOldZones)
      .then((data) => {
        const errors = get(data, 'data.errors', []);
        const successes = get(data, 'data.successes', []);
        const errorName = errors.map((err) => err.name);
        if (errors && errors.length > 0) {
          showErrorSnackbar(`Error creating: ${errorName}`);
        } else if (successes && successes.length) {
          showSuccessSnackbar(`Successfully uploaded ${successes.length} locations`);
        }
        setBulkFormOpen(false);
      })
      .then(() => fetchPickupZones(marketId))
      .catch((error) => {
        showErrorSnackbar(`Error from server: ${error}`);
      });
  };

  const activityClickHandler = (id) => {
    const vehicleZone = get(vehicleZonesById, id, {});
    // if status is unknown or active, set inactive; otherwise, set it active
    const status = get(vehicleZone, 'status');
    let newStatus;
    if (status === VehicleZonesStatusNames.ACTIVE) newStatus = VehicleZonesStatusNames.INACTIVE;
    else newStatus = VehicleZonesStatusNames.ACTIVE;
    set(vehicleZone, 'status', newStatus);
    updatePickupZone(id, vehicleZone)
      .then(() => {
        fetchPickupZones(marketId);
        showSuccessSnackbar(`${DEACTIVATE_SUCCESS} ${newStatus}`);
        setCurrentZone(null);
      })
      .catch((e) => {
        showErrorSnackbar(UPDATE_ERROR, e);
      });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <WrappedSpinner isLoading={pickupZonesLoading}>
          <Paper style={styles.root} sx={{ mt: 3 }}>
            <MUIDataTable
              title={ZONE_TABLE_TITLE}
              data={getTableData(pickupZones)}
              columns={getTableColumns(
                props,
                marketId,
                vehicleZonesById,
                deleteClickHandler,
                optionsMapClickHandler,
                activityClickHandler,
              )}
              options={getTableOptions(
                props,
                optionsAddClickHandler,
                optionsEditClickHandler,
                handleOpenBulkUploadForm,
                handleOpenDownloadOptions,
              )}
            />
          </Paper>
        </WrappedSpinner>
      </Grid>
      <Grid item xs={12}>
        {(currentZone && !warnBeforeDeleting) && (
          <Modal
            isOpen={!!currentZone}
            onSave={handleZoneSave}
            onCancel={() => {
              setCurrentZone(null);
              fetchPickupZones(marketId);
            }}
          >
            <VehicleZoneMap
              vehicleZoneId={currentZone._id}
              closeMapModal={closeMapModal}
              onChange={handleChange}
              onAutomationChange={handleAutomationChange}
              onEditBoundary={handleEditBoundary}
              onCreateBoundary={handleCreateBoundary}
              currentZone={currentZone}
            />
          </Modal>
        )}
        <WarnBeforeDeleting
          isOpen={warnBeforeDeleting}
          message={DELETE_WARN}
          onCancel={() => {
            setCurrentZone(null);
            setWarnBeforeDeleting(false);
          }}
          onConfirm={() => {
            deletePickupZone(currentZone._id)
              .then(() => {
                fetchPickupZones(marketId);
                showSuccessSnackbar(DELETE_CONFIRM);
                setCurrentZone(null);
                setWarnBeforeDeleting(false);
              })
              .catch((e) => {
                showErrorSnackbar(DELETE_ERROR, e);
              });
          }}
        />
        {bulkFormOpen && (
          <BulkUploadModal
            isOpen={bulkFormOpen}
            onCancel={handleCloseBulkUploadForm}
            onSave={handleSaveBulkUploadForm}
            type="zones"
          />
        )}

        {donwloadOptionsDisplayed && filteredZones && (
          <BulkDownloadPickupZones
            isOpen={donwloadOptionsDisplayed}
            onCancel={handleCloseDownloadOptions}
            zones={filteredZones}
            marketName={selectedMarket.name}
            tableData={getTableData(pickupZones)}
            tableColumns={getTableColumns(
              props,
              marketId,
              vehicleZonesById,
              deleteClickHandler,
              optionsMapClickHandler,
              activityClickHandler,
            )}
          />
        )}
      </Grid>
    </Grid>
  );
}

function mapStateToProps({
  markets, pickupZones, vehicleZones,
}, { match }) {
  const marketId = get(match, 'params.marketId');
  const marketName = get(match, 'params.marketName');
  const allPickupZones = get(pickupZones, 'pickupZones', []);

  const allVehicleZones = get(vehicleZones, 'vehicleZones', []);
  const filteredVehicleZones = allVehicleZones.filter(
    ({ category }) => editableVehicleZoneCategories.includes(category),
  );

  return {
    markets: markets.markets,
    selectedMarket: markets.selectedMarket,
    selectedMarketId: marketId,
    selectedMarkeName: marketName,
    pickupZonesLoading: pickupZones.isLoading,
    pickupZones: allPickupZones,
    vehicleZones: filteredVehicleZones,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    ...vehicleZoneActions,
    ...pickupZoneActions,
    ...snackbarActions,
  }, dispatch);
}

export default withPersistentSettings(
  withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
  )(VehicleZonesContainer)),
  'VehicleZoneTableKey',
);
